import React from 'react';
import { Pressable, TouchableOpacity } from 'react-native';

import { imgUser } from '@/assets/images';
import { Stack } from '@/components/layout';
import { Avatar, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { levelColor, type MatchedPlayer } from '@/features/onboarding/utils/player-card';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';

type PlayerCardProps = {
  player: MatchedPlayer;
  /** When set, the card is a queued player and shows an edit pencil that opens the edit sheet. */
  onEdit?: () => void;
  /** Tapping anywhere on the row opens the player sheet; omit for a read-only card. */
  onPress?: () => void;
};

/**
 * A single golfer row: avatar, name, level/"New" badge and current-level progress bar.
 * The left border and badge/progress colours track the player's pathway level. Queued
 * (pending) players also get an edit pencil. Shared by onboarding step 2 and the
 * "You're all set!" confirmation screen.
 */
export function PlayerCard({ player, onEdit, onPress }: PlayerCardProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  // Pending players have no real level yet, so they reuse the Level 1 brand colour.
  const color = levelColor(player.level);
  const progress = Math.max(0, Math.min(1, player.progress));

  return (
    <Pressable
      onPress={onPress}
      disabled={!onPress}
      style={({ pressed }) => [
        styles.card,
        { borderLeftColor: color },
        styles.cardRow,
        pressed && onPress ? styles.cardPressed : undefined,
      ]}>
      <Avatar
        onPress={onPress}
        size={scale(48)}
        activeOpacity={1}
        borderColor={colors['0']}
        borderWidth={0}>
        <Avatar.Image
          size={player.avatar ? undefined : scale(26)}
          disableCircular={!player.avatar}
          source={player.avatar ?? imgUser}
        />
      </Avatar>

      <Stack flex={1} gap={scale(12)}>
        <Stack direction="row" align="flex-start" justify="space-between" gap={scale(8)}>
          <Stack flex={1} direction="column" align="flex-start" gap={scale(8)}>
            <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.playerName}>
              {player.name}
            </Text>
            <Stack style={[styles.levelBadge, { backgroundColor: color }]}>
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 10 Semibold"
                style={styles.levelBadgeText}>
                {player.isPending
                  ? STRINGS.onboarding.step2.newBadge
                  : STRINGS.onboarding.step2.levelBadge.replace('{level}', String(player.level))}
              </Text>
            </Stack>
          </Stack>

          {onEdit ? (
            <TouchableOpacity onPress={onEdit} hitSlop={8}>
              <SvgIcon name="IcEdit2" size={scale(20)} color={colors.Blue} />
            </TouchableOpacity>
          ) : null}
        </Stack>

        <Stack style={styles.progressTrack}>
          <Stack
            style={[styles.progressFill, { backgroundColor: color, width: `${progress * 100}%` }]}
          />
        </Stack>
      </Stack>
    </Pressable>
  );
}

const useStyles = createStyles(({ scale }) => ({
  card: {
    backgroundColor: colors['0'],
    borderRadius: scale(12),
    borderLeftWidth: scale(2),
    paddingHorizontal: scale(12),
    paddingVertical: scale(10),
    overflow: 'hidden',
  },
  // The card root is a Pressable, so re-create the Stack row layout it would otherwise get.
  cardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(8),
  },
  cardPressed: {
    opacity: 0.7,
  },
  playerName: {
    color: colors['10'],
  },
  levelBadge: {
    paddingHorizontal: scale(8),
    paddingVertical: scale(4),
    borderRadius: scale(10),
    alignItems: 'center',
    justifyContent: 'center',
  },
  levelBadgeText: {
    color: colors['0'],
  },
  progressTrack: {
    height: scale(6),
    borderRadius: scale(10),
    backgroundColor: colors['2'],
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: scale(10),
  },
}));

export default PlayerCard;
